import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, InputGroup, Dropdown, Spinner, Pagination } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { adminService } from '../../services/adminService';
import MovieDetailsModal from './MovieDetailsModal';
import toast from 'react-hot-toast';

const Movies: React.FC = () => {
  const [movies, setMovies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGenre, setFilterGenre] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'add'>('view');
  const [selectedMovie, setSelectedMovie] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [pagination, setPagination] = useState({
    page: 1,
    limit: itemsPerPage,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    loadMovies();
  }, [currentPage, filterStatus, filterGenre]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadMovies();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadMovies = async () => {
    try {
      setLoading(true);
      const response = await adminService.getMovies({
        search: searchTerm,
        status: filterStatus === 'all' ? undefined : filterStatus,
        genre: filterGenre === 'all' ? undefined : filterGenre,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'created_at',
        sortOrder: 'desc'
      });

      if (response.success && response.data) {
        setMovies(response.data);

        const meta = response.pagination;
        const total = Number(meta?.total ?? 0);
        const limit = Number(meta?.limit ?? itemsPerPage) || itemsPerPage;
        const totalPages = Number(meta?.totalPages ?? 0);
        const page = Number(meta?.page ?? currentPage);

        setPagination({
          page,
          limit,
          total,
          totalPages,
          hasNext: Boolean(meta?.hasNext),
          hasPrev: Boolean(meta?.hasPrev),
        });
      } else {
        setPagination((prev) => ({
          ...prev,
          page: currentPage,
        }));
      }
    } catch (error: any) {
      console.error('Error loading movies:', error);
      toast.error('Failed to load movies');
      setMovies([]);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredMovies = movies;
  const totalMoviesCount = pagination.total || filteredMovies.length;
  const startItem = pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total > 0 ? Math.min(pagination.total, pagination.page * pagination.limit) : 0;

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      'Now Showing': 'success',
      'Coming Soon': 'warning',
      'Ended': 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const handleAddMovie = () => {
    setSelectedMovie(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleViewMovie = (movie: any) => {
    setSelectedMovie(movie);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditMovie = (movie: any) => {
    setSelectedMovie(movie);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteMovie = async (movie: any) => {
    if (!window.confirm(`Are you sure you want to delete movie "${movie.title}"?`)) {
      return;
    }

    try {
      const response = await adminService.deleteMovie(movie.id);
      if (response.success) {
        toast.success(`Movie "${movie.title}" deleted successfully!`);
        loadMovies();
      }
    } catch (error: any) {
      toast.error('Failed to delete movie');
    }
  };

  const handleSaveMovie = async (movieData: any) => {
    try {
      let response;
      if (modalMode === 'add') {
        response = await adminService.createMovie(movieData);
        if (response.success) {
          toast.success('Movie created successfully!');
        }
      } else {
        response = await adminService.updateMovie(selectedMovie.id, movieData);
        if (response.success) {
          toast.success('Movie updated successfully!');
        }
      }
      setShowModal(false);
      loadMovies();
    } catch (error: any) {
      toast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} movie`);
    }
  };

  const handleSyncVistaMovies = async () => {
    if (syncing) {
      return;
    }
    try {
      setSyncing(true);
      const response = await adminService.syncVistaMovies();
      if (response.success) {
        const summaryParts: string[] = [];
        const data = response.data ?? {};
        if (typeof data.inserted === 'number') {
          summaryParts.push(`${data.inserted} inserted`);
        }
        if (typeof data.updated === 'number') {
          summaryParts.push(`${data.updated} updated`);
        }
        if (typeof data.skipped === 'number') {
          summaryParts.push(`${data.skipped} skipped`);
        }
        const summary = summaryParts.length ? ` (${summaryParts.join(', ')})` : '';
        toast.success(`${response.message || 'Vista movies synced successfully'}${summary}`);
        await loadMovies();
      } else {
        toast.error(response.error || 'Failed to sync movies from Vista');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to sync movies from Vista');
    } finally {
      setSyncing(false);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber === currentPage) return;
    if (pageNumber < 1) return;
    if (pagination.totalPages && pageNumber > pagination.totalPages) return;
    setCurrentPage(pageNumber);
  };

  return (
    <>
      <Helmet>
        <title>Movies - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Movies</h2>
            <p className="text-muted mb-0">Manage movie catalog and showtimes</p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handleSyncVistaMovies}
              disabled={syncing}
            >
              {syncing ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                    role="status"
                  />
                  Syncing...
                </>
              ) : (
                <>
                  <i className="bi-arrow-repeat me-1"></i>
                  Sync from Vista
                </>
              )}
            </Button>

     {/*
<Button variant="primary" onClick={handleAddMovie}>
  <i className="bi-plus-circle me-1"></i>
  Add Movie
</Button>
*/}

          </div>
      
        </div>

        <Card>
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0">All Movies ({totalMoviesCount})</h5>
              </Col>
              <Col md={3}>
              
              <Form.Select
  value={filterStatus}
  onChange={(e) => setFilterStatus(e.target.value)}
>
  <option value="all">All</option>
  <option value="Coming Soon">Coming Soon</option>
  <option value="Now Showing">Now Showing</option>
  <option value="Ended">Ended</option>
</Form.Select>

              </Col>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search movies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Poster</th>
                  <th>Title</th>
                  <th>Genre</th>
                  <th>Duration</th>
                  <th>Rating</th>
                  <th>Language</th>
                  <th>Release Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovies.map((movie) => (
                  <tr key={movie.id}>
                    <td>
                      {movie.posterUrl ? (
                        <img
                          src={movie.posterUrl}
                          alt={movie.title}
                          style={{
                            width: "60px",
                            height: "90px",
                            objectFit: "cover",
                            borderRadius: "6px",
                          }}
                          onError={(e: any) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div
                          className="bg-light rounded d-flex align-items-center justify-content-center"
                          style={{ width: "60px", height: "90px" }}
                        >
                          <i className="bi-film text-muted"></i>
                        </div>
                      )}
                    </td>

                    <td>
                      <div>
                        <div className="fw-medium">{movie.title}</div>
                        <small className="text-muted">{movie.director}</small>
                      </div>
                    </td>
                    <td>
  <div className="text-wrap" style={{ maxWidth: "150px" }}>
    {Array.isArray(movie.genre)
      ? movie.genre.join(', ')
      : String(movie.genre ?? '').replace(/[{}"\\]/g, '').replace(/,/g, ', ')}
  </div>
</td>
                    <td>{formatDuration(movie.duration)}</td>
                    <td>
                      <Badge bg="secondary" text="light">
                        {movie.rating || "N/A"}
                      </Badge>
                    </td>
                    <td>{movie.language}</td>
                    <td>{movie.releaseDate}</td>
                    <td>{getStatusBadge(movie.status)}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="outline-secondary"
                          size="sm"
                          id={`dropdown-${movie.id}`}
                        >
                          <i className="bi-three-dots"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleViewMovie(movie)}>
                            <i className="bi-eye me-2"></i>View Details
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleEditMovie(movie)}>
                            <i className="bi-pencil me-2"></i>Edit Movie
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {/* <Dropdown.Item
                            className="text-danger"
                            onClick={() => handleDeleteMovie(movie)}
                          >
                            <i className="bi-trash me-2"></i>Delete Movie
                          </Dropdown.Item> */}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
          {pagination.totalPages > 1 && (
          <Card.Footer className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="text-muted small mb-0">
              {pagination.total > 0
                ? `Showing ${startItem}-${endItem} of ${pagination.total} movies`
                : "No movies found"}
            </div>
            <Pagination className="mb-0">
              <Pagination.Prev
                disabled={!pagination.hasPrev}
                onClick={() => handlePageChange(currentPage - 1)}
              />
              {Array.from(
                { length: pagination.totalPages },
                (_, index) => index + 1
              ).map((pageNumber) => (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === currentPage}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={!pagination.hasNext}
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </Pagination>
          </Card.Footer>
        )}
        </Card>
        

        {/* Movie Details Modal */}
        <MovieDetailsModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSaveMovie}
          movie={selectedMovie}
          mode={modalMode}
        />
      </div>
    </>
  );
};

export default Movies;
